/**
 * Online room page (/room/$code): lobby → game → next round, with chat.
 *
 * Convex hooks live in `RoomOnline`, which is mounted only when a Convex client
 * exists — so the app never calls `useQuery`/`useMutation` outside a provider.
 * All types come from the generated Convex API (`convex/_generated`).
 */

import type { End, GameMode, PlayerView } from "@domino/types";
import type { SpectatorView } from "@domino/rules-engine";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { useEffect, useMemo, useRef, useState } from "react";

import { api } from "../../../convex/convex/_generated/api";
import type { Id } from "../../../convex/convex/_generated/dataModel";
import { GameTable, type SeatInfo } from "../components/GameTable";
import { RoundEnd } from "../components/RoundEnd";
import { hasConvex } from "../lib/convex";
import { displayName, playerId, setDisplayName } from "../lib/player";

/** Rooms only accept the three modes at creation; the schema stores a string. */
function asGameMode(value: string): GameMode {
  return value === "1v1" || value === "3p" ? value : "2v2";
}

const BOARD_STYLES = ["green", "red", "sand", "night"] as const;
type BoardStyle = (typeof BOARD_STYLES)[number];

/** Felt themes ride in the room's rulesOverrides; anything unknown = green. */
function asBoardStyle(value: unknown): BoardStyle {
  return (BOARD_STYLES as readonly unknown[]).includes(value) ? (value as BoardStyle) : "green";
}

/**
 * Present a spectator with the same table UI: no hand, no legal moves.
 * (The server never sends another player's tiles — only counts.)
 */
function toPlayerView(view: PlayerView | SpectatorView, meSeat: number | null): PlayerView {
  if (meSeat !== null && "myHand" in view) return view;
  return { ...view, mySeat: meSeat ?? -1, myHand: [], legalMoves: [] };
}

/**
 * League-style chat overlay: a translucent feed that auto-fades after a few
 * quiet seconds, a 💬 pill (and the Enter key) to open the one-line input,
 * Escape/blur to close. Same component in the lobby and on the game table.
 */
function ChatOverlay({ roomId }: { roomId: Id<"rooms"> }) {
  const me = playerId();
  const messages = useQuery(api.rooms.getChat, { roomId });
  const send = useMutation(api.rooms.sendChat);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [visible, setVisible] = useState(false);
  const hideTimer = useRef<number | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const prevCount = useRef(0);

  // A fresh message reveals the feed and restarts the fade timer.
  useEffect(() => {
    if (!messages || messages.length <= prevCount.current) return;
    prevCount.current = messages.length;
    setVisible(true);
    endRef.current?.scrollIntoView({ block: "end" });
    if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setVisible(false), 7000);
  }, [messages]);

  useEffect(
    () => () => {
      if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
    },
    [],
  );

  // Enter opens the chat (only when no field or button has focus).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Enter") return;
      const el = document.activeElement as HTMLElement | null;
      if (
        el &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.isContentEditable ||
          el.tagName === "BUTTON" ||
          el.getAttribute("role") === "button")
      ) {
        return;
      }
      e.preventDefault();
      setOpen(true);
      setVisible(true);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  async function submit() {
    const text = draft.trim();
    if (!text) return;
    setDraft("");
    setOpen(false);
    try {
      await send({ roomId, playerId: me, text });
    } catch {
      /* the seat may have been released meanwhile */
    }
  }

  const shown = messages?.slice(-9) ?? [];
  return (
    <div className={`lol-chat ${open ? "chat-open" : visible ? "chat-live" : "chat-idle"}`}>
      {(open || visible) && (
        <div className="lol-feed">
          {shown.length > 0 ? (
            shown.map((m) => (
              <div className="lol-line" key={m.id}>
                <span className="lol-name">({m.displayName}):</span> {m.text}
              </div>
            ))
          ) : (
            <div className="lol-line lol-muted">No messages yet — say hello 👋</div>
          )}
          <div ref={endRef} />
        </div>
      )}
      {open ? (
        <input
          className="lol-input"
          autoFocus
          placeholder="Say something…  (Enter to send · Esc to close)"
          value={draft}
          maxLength={240}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void submit();
            }
            if (e.key === "Escape") {
              e.preventDefault();
              setOpen(false);
            }
          }}
          onBlur={() => setOpen(false)}
        />
      ) : (
        <button
          type="button"
          className="lol-chat-btn"
          onClick={() => {
            setOpen(true);
            setVisible(true);
          }}
        >
          💬 Chat <span className="lol-chat-hint">Enter</span>
        </button>
      )}
    </div>
  );
}

function RoomOnline({ code }: { code: string }) {
  const me = playerId();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(displayName());
  const joined = useRef(false);

  const lobby = useQuery(api.rooms.getLobby, { code, playerId: me });
  const roomId = lobby?.room.id;
  const game = useQuery(
    api.game.getGameView,
    roomId !== undefined ? { roomId, playerId: me } : "skip",
  );

  const joinRoom = useMutation(api.rooms.joinRoom);
  const leaveRoom = useMutation(api.rooms.leaveRoom);
  const renamePlayer = useMutation(api.rooms.renamePlayer);
  const setReady = useMutation(api.rooms.setReady);
  const setSeat = useMutation(api.rooms.setSeat);
  const updateRules = useMutation(api.rooms.updateRules);
  const startMatch = useMutation(api.rooms.startMatch);
  const playTile = useMutation(api.game.playTile);
  const skipStaleTurn = useMutation(api.game.skipStaleTurn);
  const heartbeat = useMutation(api.rooms.heartbeat);
  const sweepRoom = useMutation(api.rooms.sweepRoom);
  const drawTile = useMutation(api.game.drawTile);
  const passTurn = useMutation(api.game.passTurn);
  const nextRound = useMutation(api.game.nextRound);

  const playing = game != null && game.phase === "playing" ? game : null;
  const lobbyPhase = game != null && game.phase === "lobby" ? game : null;
  const view = playing ? toPlayerView(playing.view, playing.meSeat) : null;
  const mode = asGameMode(lobby?.room.mode ?? "2v2");
  const seated = playing
    ? playing.meSeat !== null
    : (lobbyPhase?.meSeat ?? lobby?.meSeat ?? null) !== null;

  // Arriving from an invite link: claim a seat automatically.
  useEffect(() => {
    if (!lobby || !lobbyPhase || joined.current) return;
    if (lobby.room.status !== "lobby") return;
    if (lobbyPhase.players.some((seat) => seat.playerId === me)) return;
    if (lobby.meSeat !== null) return;
    joined.current = true;
    void joinRoom({ code, playerId: me, displayName: displayName() || "Player" }).catch((err) => {
      setError(err instanceof Error ? err.message : "Could not join the room");
    });
  }, [code, joinRoom, lobby, lobbyPhase, me]);

  async function saveName() {
    const name = nameDraft.trim();
    if (!name) return;
    setDisplayName(name);
    setEditingName(false);
    try {
      await renamePlayer({ playerId: me, displayName: name });
    } catch (err) {
      setError(
        `The table could not save your new name (${err instanceof Error ? err.message : "backend unreachable"}) — re-sync the Convex functions and try again.`,
      );
    }
  }

  // Presence: ping while in the room; sweep abandoned tables (30 min quiet).
  useEffect(() => {
    if (!lobby) return;
    void heartbeat({ roomId: lobby.room.id, playerId: me }).catch(() => {});
    const beat = window.setInterval(() => {
      void heartbeat({ roomId: lobby.room.id, playerId: me }).catch(() => {});
      void sweepRoom({ roomId: lobby.room.id }).catch(() => {});
    }, 15_000);
    return () => window.clearInterval(beat);
  }, [heartbeat, lobby, me, sweepRoom]);

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    }
  }

  const seats: SeatInfo[] = useMemo(() => {
    if (!playing && !lobbyPhase) return [];
    const players = (playing ?? lobbyPhase)?.players ?? [];
    const counts = playing?.view.handCounts ?? [];
    return players.map((seat) => ({
      seatIndex: seat.seatIndex,
      name: seat.displayName ?? "Open seat",
      team: seat.team,
      count: counts[seat.seatIndex] ?? 0,
      isTurn:
        playing !== null &&
        playing.view.currentSeat === seat.seatIndex &&
        playing.view.status === "active",
      isMe: seat.playerId === me,
      connected: seat.connected !== false,
    }));
  }, [lobbyPhase, me, playing]);

  /** One chair around the brass table: photo card with the player overlaid. */
  function renderChair(
    seat?: {
      seatIndex: number;
      team: number | null;
      playerId: string | null;
      displayName: string | null;
      ready: boolean;
      connected?: boolean;
    },
    roomId?: Id<"rooms">,
  ) {
    if (!seat || !roomId) return null;
    const isMine = seat.playerId === me;
    const team = seat.team ?? seat.seatIndex % 2;
    return (
      <div
        key={seat.seatIndex}
        className={`lobby-chair chair-t${team + 1} ${isMine ? "chair-mine" : ""} ${
          seat.playerId === null ? "chair-open" : "chair-occupied"
        } ${seat.connected === false ? "chair-away" : ""}`}
      >
        <span className="lobby-chair-img" aria-hidden />
        <div className="lobby-chair-info">
          <span className="lobby-chair-name">{seat.displayName ?? "Empty chair"}</span>
          <span className="lobby-chair-meta">
            Seat {seat.seatIndex + 1} · Team {team + 1}
            {seat.connected === false ? " · away" : ""}
          </span>
          {seat.ready && <span className="lobby-chair-ready">✓ ready</span>}
          {seat.playerId === null && (
            <button
              type="button"
              className="btn-gold-primary lobby-chair-take"
              onClick={() =>
                void run(() =>
                  setSeat({
                    roomId,
                    playerId: me,
                    seatIndex: seat.seatIndex,
                    displayName: displayName() || undefined,
                  }),
                )
              }
            >
              Take this chair
            </button>
          )}
        </div>
      </div>
    );
  }

  if (lobby === undefined) {
    return (
      <div className="layout">
        <div className="card">
          <span className="muted">Loading room…</span>
        </div>
      </div>
    );
  }

  if (lobby === null) {
    return (
      <div className="layout">
        <div className="card">
          <h1>Room not found</h1>
          <p className="muted">No open room with the code {code}.</p>
          <button type="button" onClick={() => void navigate({ to: "/" })}>
            ← Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {error ? (
        <div className="layout" style={{ paddingBottom: 0 }}>
          <div className="card error">{error}</div>
        </div>
      ) : null}

      {playing && view ? (
        <GameTable
          view={view}
          mode={mode}
          boardStyle={asBoardStyle(lobby.room.rulesOverrides?.boardStyle)}
          turnStale={
            typeof playing.turnStartedAt === "number" && Date.now() - playing.turnStartedAt > 45_000
          }
          onSkipStale={() => {
            void run(() => skipStaleTurn({ roomId: lobby.room.id, playerId: me }));
          }}
          seats={seats}
          scores={playing.match.scores}
          targetScore={playing.match.targetScore}
          title={`Room ${lobby.room.code}`}
          onLeave={() => void navigate({ to: "/" })}
          onPlay={(tileId: number, end?: End) => {
            void run(() =>
              playTile({
                roundId: playing.roundId,
                playerId: me,
                tileId,
                ...(end !== undefined ? { end } : {}),
                expectedTurnNumber: view.turnNumber,
              }),
            );
          }}
          onDraw={() => {
            void run(() =>
              drawTile({
                roundId: playing.roundId,
                playerId: me,
                expectedTurnNumber: view.turnNumber,
              }),
            );
          }}
          onPass={() => {
            void run(() =>
              passTurn({
                roundId: playing.roundId,
                playerId: me,
                expectedTurnNumber: view.turnNumber,
              }),
            );
          }}
          onNextRound={() => {
            void run(() => nextRound({ matchId: playing.match.id, playerId: me }));
          }}
          canNextRound={playing.match.status !== "finished"}
          statusNote={
            playing.match.status === "finished" ? (
              <div className="banner">
                <strong>
                  Match finished —{" "}
                  {lobby.room.mode === "2v2"
                    ? `team ${(playing.match.winner ?? 0) + 1} wins`
                    : `${seats.find((s) => s.seatIndex === playing.match.winner)?.name ?? "The table"} wins`}
                  .
                </strong>
              </div>
            ) : view.result ? (
              <RoundEnd
                mode={mode}
                seats={seats}
                mySeat={playing.meSeat ?? -1}
                result={view.result}
                hands={playing.reveals?.perSeatTiles}
                pips={playing.reveals?.perSeatPipCounts}
              />
            ) : !seated ? (
              <div className="muted">You are watching this table.</div>
            ) : null
          }
          footer={<ChatOverlay roomId={lobby.room.id} />}
        />
      ) : (
        <div className="lobby-root">
          <div className="lobby-backdrop" aria-hidden />
          <div className="lobby-vignette" aria-hidden />
          <button
            type="button"
            className="lobby-back"
            onClick={() =>
              void run(async () => {
                await leaveRoom({ roomId: lobby.room.id, playerId: me });
                await navigate({ to: "/" });
              })
            }
          >
            ← Leave
          </button>
          <div className="lobby-content">
            <header className="lobby-header">
              <span className="lobby-lamp" aria-hidden />
              <div className="lobby-header-titles">
                <h1 className="lobby-title">Table {lobby.room.code}</h1>
                <span className="lobby-sub">
                  Cairo street ahwa · {lobby.room.mode} ·{" "}
                  {lobby.seats.filter((seat) => seat.occupied).length}/{lobby.seats.length} seated
                </span>
                {editingName ? (
                  <form
                    className="lobby-me"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void saveName();
                    }}
                  >
                    <input
                      className="lobby-me-input"
                      autoFocus
                      value={nameDraft}
                      maxLength={18}
                      placeholder="Your name"
                      onChange={(e) => setNameDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") setEditingName(false);
                      }}
                    />
                    <button type="submit" className="lobby-me-save">
                      Save
                    </button>
                  </form>
                ) : (
                  <span className="lobby-me">
                    Playing as <b>{displayName() || "Player"}</b>
                    <button
                      type="button"
                      className="lobby-rename-btn"
                      onClick={() => {
                        setNameDraft(displayName());
                        setEditingName(true);
                      }}
                    >
                      Change name
                    </button>
                  </span>
                )}
              </div>
            </header>

            <section className="lobby-scene" aria-label="Table seats">
              <div className="lobby-chairs lobby-chairs-left">
                {renderChair(lobby.seats[0], lobby.room.id)}
                {renderChair(lobby.seats[2], lobby.room.id)}
              </div>

              <div className="lobby-table-wrap">
                <div className="lobby-table" role="img" aria-label={`Table code ${lobby.room.code}`}>
                  <span className="lobby-table-code">{lobby.room.code}</span>
                </div>
                <div className="lobby-table-actions">
                  <button
                    type="button"
                    className="btn-silver-secondary"
                    onClick={() => {
                      void navigator.clipboard?.writeText(location.href);
                      setCopied(true);
                      window.setTimeout(() => setCopied(false), 1800);
                    }}
                  >
                    {copied ? "✓ Link copied" : "Copy invite link"}
                  </button>

                </div>
              </div>

              <div className="lobby-chairs lobby-chairs-right">
                {renderChair(lobby.seats[1], lobby.room.id)}
                {renderChair(lobby.seats[3], lobby.room.id)}
              </div>
            </section>

            {lobby.room.hostUserId === me && (
              <div className="lobby-board-picker">
                <span className="picker-label">Table felt</span>
                <div className="picker-row">
                  {([["green", "Green"], ["red", "Crimson"], ["sand", "Desert"], ["night", "Midnight"]] as const).map(
                    ([id, label]) => (
                      <button
                        key={id}
                        type="button"
                        className={`felt-swatch felt-${id} ${
                          asBoardStyle(lobby.room.rulesOverrides?.boardStyle) === id ? "swatch-active" : ""
                        }`}
                        onClick={() =>
                          void run(() =>
                            updateRules({
                              roomId: lobby.room.id,
                              playerId: me,
                              overrides: { ...(lobby.room.rulesOverrides ?? {}), boardStyle: id },
                            }),
                          )
                        }
                      >
                        <i aria-hidden />
                        <span>{label}</span>
                      </button>
                    ),
                  )}
                </div>
                <span className="picker-note">host only · readies reset</span>
              </div>
            )}

            <div className="lobby-actions">
              <button
                type="button"
                className={
                  lobby.seats.find((seat) => seat.playerId === me)?.ready
                    ? "btn-silver-secondary"
                    : "btn-gold-primary"
                }
                onClick={() => {
                  const mine = lobby.seats.find((seat) => seat.playerId === me);
                  void run(() =>
                    setReady({
                      roomId: lobby.room.id,
                      playerId: me,
                      ready: !mine?.ready,
                      displayName: displayName() || undefined,
                    }),
                  );
                }}
              >
                {lobby.seats.find((seat) => seat.playerId === me)?.ready
                  ? "Cancel ready"
                  : "✓ I'm ready"}
              </button>

              {lobby.room.hostUserId === me ? (
                <button
                  type="button"
                  className="btn-gold-large"
                  disabled={lobby.seats.some((seat) => !seat.occupied)}
                  onClick={() =>
                    void run(() => startMatch({ roomId: lobby.room.id, playerId: me }))
                  }
                >
                  Start the match →
                </button>
              ) : (
                <span className="lobby-waiting">Waiting for the table host to begin…</span>
              )}
            </div>

            <ChatOverlay roomId={lobby.room.id} />
          </div>
        </div>
      )}
    </>
  );
}

export default function RoomPage() {
  const params = useParams({ from: "/room/$code" });
  const code = params.code.toUpperCase();

  if (!hasConvex) {
    return (
      <div className="layout">
        <div className="card">
          <h1>Online rooms are off</h1>
          <p className="muted">
            Start a Convex backend (<span className="mono">bunx convex dev</span> in{" "}
            <span className="mono">packages/convex</span>), set{" "}
            <span className="mono">VITE_CONVEX_URL</span> in{" "}
            <span className="mono">packages/client/.env.local</span>, then reload. You can play a
            local hot-seat game from the home page meanwhile.
          </p>
        </div>
      </div>
    );
  }

  return <RoomOnline code={code} />;
}
